/**
 * BenchClearers CRO event tracking — fires GA4 custom events for the
 * recommendations + filter drawer instrumentation needed by the bi-weekly
 * CRO Impact Audit.
 *
 * Events emitted (all to GA4 via gtag):
 *   - view_item_recommendations  : "Fans Also Rep" enters viewport (once per page)
 *   - select_recommendation      : User clicks a rec product card
 *   - view_filter_drawer         : Filter drawer opens
 *   - apply_filter               : User toggles a filter checkbox on
 *   - clear_all_filters          : User clicks the "Clear all filters" button
 *   - add_to_cart_from_recommendation : ATC happens within 30 min of a rec click
 *
 * Self-contained, no dependencies. Idempotent — listeners are added once.
 * Safe to load on any page (handlers exit cleanly when target elements absent).
 */
(function () {
  'use strict';

  // Bail if gtag isn't loaded for some reason
  if (typeof gtag !== 'function') {
    console.warn('[cro-events] gtag not found; events will not fire');
    return;
  }

  var REC_CLICK_KEY = 'bc_rec_click';
  var REC_CLICK_TTL_MS = 30 * 60 * 1000;  // 30 min window for "from rec" ATC

  // ------------- 1. view_item_recommendations -------------
  function trackRecsViewport() {
    var recsSection = document.querySelector('product-recommendations, .product-recommendations, [data-section-type="product-recommendations"]');
    if (!recsSection) return;

    var fired = false;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting && !fired) {
          fired = true;
          gtag('event', 'view_item_recommendations', {
            page_location: window.location.pathname,
            recs_count: recsSection.querySelectorAll('.grid__item, .product-card').length || null,
          });
          io.disconnect();
        }
      });
    }, { threshold: 0.4 });
    io.observe(recsSection);
  }

  // ------------- 2. select_recommendation -------------
  function trackRecsClicks() {
    var recsSection = document.querySelector('product-recommendations, .product-recommendations, [data-section-type="product-recommendations"]');
    if (!recsSection) return;

    recsSection.addEventListener('click', function (e) {
      var link = e.target.closest('a[href*="/products/"]');
      if (!link) return;
      var handle = (link.getAttribute('href').match(/\/products\/([^/?#]+)/) || [])[1];
      if (!handle) return;

      gtag('event', 'select_recommendation', {
        item_id: handle,
        page_location: window.location.pathname,
        link_text: (link.textContent || '').trim().slice(0, 80),
      });

      // Stash for add_to_cart_from_recommendation tracking
      try {
        sessionStorage.setItem(REC_CLICK_KEY, JSON.stringify({
          handle: handle,
          ts: Date.now(),
        }));
      } catch (_) { /* sessionStorage may be disabled */ }
    });
  }

  // ------------- 3. view_filter_drawer -------------
  function trackFilterDrawerOpen() {
    var trigger = document.querySelector('#FilterDrawerTrigger, .js-drawer-open-collection-filters');
    if (!trigger) return;

    trigger.addEventListener('click', function () {
      gtag('event', 'view_filter_drawer', {
        page_location: window.location.pathname,
      });
    });
  }

  // ------------- 4. apply_filter -------------
  function trackFilterApply() {
    var drawer = document.querySelector('#FilterDrawer');
    if (!drawer) return;

    drawer.addEventListener('change', function (e) {
      var input = e.target;
      if (!input || input.type !== 'checkbox') return;
      if (!input.classList.contains('tag__input')) return;
      // Only fire on toggle-on (apply), not toggle-off (remove)
      if (!input.checked) return;

      var name = input.getAttribute('name') || '';
      var value = input.getAttribute('value') || '';
      gtag('event', 'apply_filter', {
        filter_name: name,
        filter_value: value,
        filter_type: name.replace('filter.p.m.custom.', '').replace('filter.p.', ''),
        page_location: window.location.pathname,
      });
    });
  }

  // ------------- 5. clear_all_filters -------------
  function trackClearAll() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.filter-clear-all-btn');
      if (!btn) return;
      // Count active filters from the drawer for context
      var activeChips = document.querySelectorAll('#FilterDrawer .tag--remove').length || null;
      gtag('event', 'clear_all_filters', {
        active_filters_cleared: activeChips,
        page_location: window.location.pathname,
      });
    });
  }

  // ------------- 6. add_to_cart_from_recommendation -------------
  function trackAtcFromRec() {
    function checkAndFire() {
      var stash;
      try { stash = JSON.parse(sessionStorage.getItem(REC_CLICK_KEY) || 'null'); } catch (_) { return; }
      if (!stash || !stash.handle || !stash.ts) return;
      if (Date.now() - stash.ts > REC_CLICK_TTL_MS) return;

      // Match: are we currently on the product the rec sent us to?
      var currentHandle = (window.location.pathname.match(/\/products\/([^/?#]+)/) || [])[1];
      if (currentHandle !== stash.handle) return;

      gtag('event', 'add_to_cart_from_recommendation', {
        item_id: currentHandle,
        rec_click_age_ms: Date.now() - stash.ts,
      });

      // Clear so we don't double-fire on same rec click
      try { sessionStorage.removeItem(REC_CLICK_KEY); } catch (_) {}
    }

    // Path 1: form submit (works on themes without AJAX cart)
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || form.action === undefined) return;
      if (!/\/cart\/add/.test(form.action)) return;
      checkAndFire();
    });

    // Path 2: wrap fetch — catches AJAX ATC (Motion theme + Upcart use this)
    if (window.fetch) {
      var origFetch = window.fetch;
      window.fetch = function () {
        var url = arguments[0];
        var urlStr = typeof url === 'string' ? url : (url && url.url) || '';
        var isAtc = /\/cart\/add(\.js)?(\?|$)/.test(urlStr);
        var result = origFetch.apply(this, arguments);
        if (isAtc) {
          result.then(function (resp) {
            if (resp && resp.ok) checkAndFire();
          }).catch(function () { /* swallow */ });
        }
        return result;
      };
    }

    // Path 3: wrap XHR — fallback for any code path using XMLHttpRequest
    if (window.XMLHttpRequest) {
      var origOpen = XMLHttpRequest.prototype.open;
      XMLHttpRequest.prototype.open = function (method, url) {
        this._croAtcUrl = url;
        return origOpen.apply(this, arguments);
      };
      var origSend = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.send = function () {
        var xhr = this;
        if (xhr._croAtcUrl && /\/cart\/add(\.js)?(\?|$)/.test(xhr._croAtcUrl)) {
          xhr.addEventListener('load', function () {
            if (xhr.status >= 200 && xhr.status < 300) checkAndFire();
          });
        }
        return origSend.apply(this, arguments);
      };
    }
  }

  // ------------- Boot -------------
  function init() {
    trackRecsViewport();
    trackRecsClicks();
    trackFilterDrawerOpen();
    trackFilterApply();
    trackClearAll();
    trackAtcFromRec();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
